import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import {
  collection,
  getDocs,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/fire";
import moment from 'moment-timezone';

const TZ = 'Pacific/Auckland';

const ClassAccordionContext = createContext();

export const useClassAccordion = () => useContext(ClassAccordionContext);

export const ClassAccordionProvider = ({ children }) => {
  const [todayClasses, setTodayClasses] = useState([]);
  const [expandedClassId, setExpandedClassId] = useState(null);
  const classAttendanceRef = useRef({});
  const [classAttendance, setClassAttendance] = useState({});
  const todayClassesRef = useRef([]);

  useEffect(() => {
    todayClassesRef.current = todayClasses;
  }, [todayClasses]);

  useEffect(() => {
    let statusUpdateInterval = null;
    let worklogUnsubscribe = null;

    const updateClassStatuses = (classes) => {
      const now = moment().tz(TZ);
      const classDate = now.format('YYYY-MM-DD');
      return classes.map(cls => {
        const start = moment.tz(`${classDate} ${cls.startTime}`, 'YYYY-MM-DD HH:mm', TZ);
        const end = moment.tz(`${classDate} ${cls.endTime}`, 'YYYY-MM-DD HH:mm', TZ);
        return {
          ...cls,
          status: now.isBetween(start, end) ? 'current' :
            now.isBefore(start) ? 'upcoming' : 'past',
          timeUntilStart: now.isBefore(start) ?
            moment.duration(start.diff(now)).format("h [hr] m [min]") : null
        };
      });
    };

    const fetchAttendanceFromClassSchedule = async (displayedClass, isCurrentClass = false) => {
      try {
        const currentDate = moment().tz('Pacific/Auckland');
        const todayDateString = currentDate.format('YYYY-MM-DD');

        const attendanceData = displayedClass.attendance && typeof displayedClass.attendance === 'object'
          ? displayedClass.attendance[todayDateString]
          : [];

        const attendees = await Promise.all(
          attendanceData?.map(async (entry) => {
            try {
              const userDoc = await getDoc(doc(db, "members", entry.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  name: userData.name || "Unknown",
                  lastName: userData.lastName || "",
                  time: entry.time || "N/A",
                  userId: entry.userId,
                };
              } else {
                console.warn(`No user found with ID: ${entry.userId}`);
                return null;
              }
            } catch (error) {
              console.error(`Error fetching user with ID: ${entry.userId}`, error);
              return null;
            }
          }) ?? []
        );
        const filteredAttendees = attendees.filter(Boolean);

        const worklogRef = doc(db, "worklog", todayDateString);
        const worklogSnap = await getDoc(worklogRef);
        let coachLoginTimes = {};

        if (worklogSnap.exists()) {
          const worklogData = worklogSnap.data();
          displayedClass.coaches.forEach(coach => {
            if (worklogData[coach] && worklogData[coach][displayedClass.id]) {
              const loginTime = worklogData[coach][displayedClass.id][0].loginTime.toDate();
              coachLoginTimes[coach] = moment(loginTime).format('h:mm:ss a');
            } else {
              coachLoginTimes[coach] = "Not logged in";
            }
          });
        } else {
          // If no worklog exists, mark all coaches as not logged in
          displayedClass.coaches.forEach(coach => {
            coachLoginTimes[coach] = "Not logged in";
          });
        }

        // FIXED: Only update sessionStorage for the CURRENT active class
        if (isCurrentClass) {
          sessionStorage.setItem('instructorLoginTimes', JSON.stringify(coachLoginTimes));
          console.log('ClassAccordionContext: Set instructorLoginTimes for class:', displayedClass.className, coachLoginTimes);
        }

        setClassAttendance((prev) => ({
          ...prev,
          [displayedClass.id]: {
            className: displayedClass.className || "Unnamed Class",
            startTime: displayedClass.startTime || "N/A",
            endTime: displayedClass.endTime || "N/A",
            attendees: filteredAttendees,
            totalAttendees: filteredAttendees.length,
            totalSpots: displayedClass.availableSpots || 0,
            availableSpots: (displayedClass.availableSpots || 0) - filteredAttendees.length,
            coaches: displayedClass.coaches || [],
            coachLoginTimes: coachLoginTimes,
          }
        }));

        classAttendanceRef.current = {
          ...classAttendanceRef.current,
          [displayedClass.id]: {
            className: displayedClass.className || "Unnamed Class",
            startTime: displayedClass.startTime || "N/A",
            endTime: displayedClass.endTime || "N/A",
            attendees: filteredAttendees,
            totalAttendees: filteredAttendees.length,
            totalSpots: displayedClass.availableSpots || 0,
            availableSpots: (displayedClass.availableSpots || 0) - filteredAttendees.length,
            coaches: displayedClass.coaches || [],
            coachLoginTimes: coachLoginTimes,
          }
        };

      } catch (error) {
        console.error("Error fetching attendance from classSchedule:", error);
      }
    };

    const classScheduleRef = collection(db, "classSchedule");

    const unsubscribe = onSnapshot(classScheduleRef, async (snapshot) => {
      try {
       
        const classSchedule = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const currentDate = moment().tz(TZ);
        const currentTime = currentDate.format('HH:mm');
        const currentDay = currentDate.format('dddd');
        const todayDateString = currentDate.format('YYYY-MM-DD');

        const processedClasses = classSchedule.filter(cls => {
          const isSpecialClassToday = cls.isSpecialClass && cls.specialClassDate === todayDateString;
          const isRegularClassToday = !cls.isSpecialClass && cls.days.includes(currentDay);
          return isSpecialClassToday || isRegularClassToday;
        }).sort((a, b) => moment(a.startTime, 'HH:mm') - moment(b.startTime, 'HH:mm'))
          .map(cls => {
            const start = moment.tz(`${todayDateString} ${cls.startTime}`, 'YYYY-MM-DD HH:mm', TZ);
            const end = moment.tz(`${todayDateString} ${cls.endTime}`, 'YYYY-MM-DD HH:mm', TZ);
            const now = moment().tz(TZ);
            return {
              ...cls,
              status: now.isBetween(start, end) ? 'current' :
                now.isBefore(start) ? 'upcoming' : 'past',
              timeUntilStart: now.isBefore(start) ?
                moment.duration(start.diff(now)).format("h [hr] m [min]") : null
            };
          });

        statusUpdateInterval = setInterval(() => {
          setTodayClasses(prevClasses => {
            const updatedClasses = updateClassStatuses(prevClasses);
            const filteredClasses = updatedClasses.filter(cls => cls.status !== 'past');
            
            // Check if we have a new current class and update sessionStorage
            const newCurrentClass = filteredClasses.find(cls => cls.status === 'current');
            const storedClass = sessionStorage.getItem('currentClass');
            const storedClassId = storedClass ? JSON.parse(storedClass).id : null;
            
            if (newCurrentClass && newCurrentClass.id !== storedClassId) {
              // New class just became current - update sessionStorage
              sessionStorage.setItem('currentClass', JSON.stringify(newCurrentClass));
              console.log('Class status changed to current:', newCurrentClass.className);
              
              // Refetch attendance for this class to update coach login times
              fetchAttendanceFromClassSchedule(newCurrentClass, true);
            } else if (!newCurrentClass && storedClassId) {
              // No current class anymore but we had one stored
              const stillExists = filteredClasses.find(cls => cls.id === storedClassId);
              if (!stillExists) {
                sessionStorage.removeItem('currentClass');
                sessionStorage.removeItem('instructorLoginTimes');
                console.log('Current class ended and removed from storage');
              }
            }
            
            return filteredClasses;
          });
        }, 30000); // Update every 30 seconds

        const relevantClasses = processedClasses.filter(cls => cls.status !== 'past');
        setTodayClasses(relevantClasses);

        if (!worklogUnsubscribe) {
          const worklogRef = doc(db, "worklog", todayDateString);
          worklogUnsubscribe = onSnapshot(worklogRef, (worklogSnap) => {
            const worklogData = worklogSnap.exists() ? worklogSnap.data() : {};
            const currentClasses = todayClassesRef.current;

            setClassAttendance((prev) => {
              const updated = { ...prev };

              currentClasses.forEach((cls) => {
                const coachLoginTimes = {};
                (cls.coaches || []).forEach((coach) => {
                  if (worklogData[coach] && worklogData[coach][cls.id]) {
                    const loginTime = worklogData[coach][cls.id][0]?.loginTime?.toDate?.();
                    coachLoginTimes[coach] = loginTime
                      ? moment(loginTime).format('h:mm:ss a')
                      : "Not logged in";
                  } else {
                    coachLoginTimes[coach] = "Not logged in";
                  }
                });

                if (updated[cls.id]) {
                  updated[cls.id] = {
                    ...updated[cls.id],
                    coachLoginTimes
                  };
                } else {
                  updated[cls.id] = {
                    className: cls.className || "Unnamed Class",
                    startTime: cls.startTime || "N/A",
                    endTime: cls.endTime || "N/A",
                    attendees: [],
                    totalAttendees: 0,
                    totalSpots: cls.availableSpots || 0,
                    availableSpots: cls.availableSpots || 0,
                    coaches: cls.coaches || [],
                    coachLoginTimes
                  };
                }
              });

              return updated;
            });

            const currentClass = currentClasses.find(cls => cls.status === 'current');
            if (currentClass) {
              const currentTimes = {};
              (currentClass.coaches || []).forEach((coach) => {
                if (worklogData[coach] && worklogData[coach][currentClass.id]) {
                  const loginTime = worklogData[coach][currentClass.id][0]?.loginTime?.toDate?.();
                  currentTimes[coach] = loginTime
                    ? moment(loginTime).format('h:mm:ss a')
                    : "Not logged in";
                } else {
                  currentTimes[coach] = "Not logged in";
                }
              });
              sessionStorage.setItem('instructorLoginTimes', JSON.stringify(currentTimes));
            }
          }, (error) => {
            console.error("Error listening to worklog:", error);
          });
        }

        // Find the current active class
        const firstCurrent = relevantClasses.find(cls => cls.status === 'current');
        
        console.log('ClassAccordionContext: Found current class:', firstCurrent ? firstCurrent.className : 'None');
        
        // Auto-expand the current class
        if (firstCurrent) {
          setExpandedClassId(firstCurrent.id);
        }
        
        if (firstCurrent) {
          // Update currentClass in sessionStorage
          sessionStorage.setItem('currentClass', JSON.stringify(firstCurrent));
          console.log('ClassAccordionContext: Set currentClass in sessionStorage:', firstCurrent.className);
          
          // Fetch attendance for all classes, but mark which one is current
          await Promise.all(relevantClasses.map(cls => 
            fetchAttendanceFromClassSchedule(cls, cls.id === firstCurrent.id)
          ));
        } else {
          // No current class - check if we need to clear old data
          const storedClass = sessionStorage.getItem('currentClass');
          if (storedClass) {
            const stored = JSON.parse(storedClass);
            const stillExists = relevantClasses.find(cls => cls.id === stored.id);
            
            console.log('ClassAccordionContext: No current class, checking if stored class still exists:', stillExists ? 'Yes' : 'No');
            
            // Only clear if the stored class is completely gone (not even upcoming)
            if (!stillExists) {
              sessionStorage.removeItem('currentClass');
              sessionStorage.removeItem('instructorLoginTimes');
              console.log('ClassAccordionContext: Cleared sessionStorage');
            }
          }
          
          // Fetch attendance for all classes (none are current)
          await Promise.all(relevantClasses.map(cls => 
            fetchAttendanceFromClassSchedule(cls, false)
          ));
        }

      } catch (error) {
        console.error("Error fetching class information:", error);
      }
    }, (error) => {
      console.error("Error listening to class schedule:", error);
    });

    return () => {
      unsubscribe();
      if (worklogUnsubscribe) worklogUnsubscribe();
      if (statusUpdateInterval) clearInterval(statusUpdateInterval);
    };
  }, []);

  const value = {
    todayClasses,
    setTodayClasses,
    expandedClassId,
    setExpandedClassId,
    classAttendance,
    setClassAttendance
  };

  return (
    <ClassAccordionContext.Provider value={value}>
      {children}
    </ClassAccordionContext.Provider>
  );
};